import React from 'react';
import { Card, Divider, Grid, Group, Stack, Table, Text } from '@mantine/core';
import type { ICotizacionPostventa } from '../../../modelos/CotizadorPostventa/entidades/ICotizacionPostventa';
import type { IResultadoCotizacionPostventa } from '../../../modelos/CotizadorPostventa/entidades/IResultadoCotizacionPostventa';
import { formatoMoneda } from '../interfaces';

interface ResumenComercialPostventaVistaProps {
    cotizacion: ICotizacionPostventa;
    resultado: IResultadoCotizacionPostventa;
    incoterms: { value: string; label: string }[];
}

const Dato: React.FC<{ etiqueta: string; valor?: string | number }> = ({ etiqueta, valor }) => (
    <Grid.Col span={{ base: 6, md: 3 }}>
        <Text size="xs" c="dimmed">{etiqueta}</Text>
        <Text size="sm" fw={500}>{valor || '-'}</Text>
    </Grid.Col>
);

/** Sección de la carátula: ítems con su detalle, sin costos. */
const SeccionItems: React.FC<{ titulo: string; columnas: string[]; filas: (string | number)[][] }> = ({ titulo, columnas, filas }) => {
    if (filas.length === 0) return null;
    return (
        <div>
            <Text fw={600} size="sm" mb={4}>{titulo}</Text>
            <Table withTableBorder withColumnBorders fz="xs" verticalSpacing={4}>
                <Table.Thead>
                    <Table.Tr>{columnas.map(c => <Table.Th key={c}>{c}</Table.Th>)}</Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {filas.map((fila, i) => (
                        <Table.Tr key={i}>{fila.map((celda, j) => <Table.Td key={j}>{celda === '' ? '-' : celda}</Table.Td>)}</Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        </div>
    );
};

/** Carátula para el cliente (hoja PRECIO): qué incluye el trabajo, precio y plazos. No muestra costos. */
export const ResumenComercialPostventaVista: React.FC<ResumenComercialPostventaVistaProps> = ({ cotizacion, resultado, incoterms }) => {
    const unidad = cotizacion.UnidadTiempo.toLowerCase();
    const conceptos = (lista: ICotizacionPostventa['Transportes']) => lista.map(c => [c.Item, c.Detalle]);

    return (
        <Stack gap="md">
            <Card withBorder radius="md" p="md">
                <Grid>
                    <Dato etiqueta="Proyecto" valor={cotizacion.Proyecto} />
                    <Dato etiqueta="Cliente" valor={cotizacion.Cliente} />
                    <Dato etiqueta="Fecha" valor={cotizacion.FechaCreacion ? new Date(cotizacion.FechaCreacion).toLocaleDateString('es-CO') : undefined} />
                    <Dato etiqueta="Elaboró" valor={cotizacion.UsuarioCreadorNombre} />
                </Grid>
                <Divider my="sm" />
                <Text size="xs" c="dimmed">Descripción del trabajo</Text>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{cotizacion.Descripcion || '-'}</Text>
            </Card>

            <Card withBorder radius="md" p="md">
                <Stack gap="sm">
                    <SeccionItems
                        titulo="Repuestos por unidad"
                        columnas={['Ítem', 'Cantidad', 'Incoterm', 'Detalle']}
                        filas={cotizacion.Repuestos.map(r => [r.Item, r.Cantidad, incoterms.find(i => i.value === r.IncotermId)?.label ?? '', r.Detalle])}
                    />
                    {(cotizacion.PesoGuacalKg > 0 || cotizacion.DimensionesGuacal) && (
                        <SeccionItems
                            titulo="Guacal (escenario máximo)"
                            columnas={['Peso de la carga', 'Dimensiones']}
                            filas={[[cotizacion.PesoGuacalKg ? `${cotizacion.PesoGuacalKg.toLocaleString('es-CO')} kg` : '', cotizacion.DimensionesGuacal]]}
                        />
                    )}
                    <SeccionItems titulo="Horas de trabajo por unidad" columnas={['Ítem', 'Detalle']} filas={[['Horas hombre', cotizacion.DetalleManoObra]]} />
                    <SeccionItems titulo="Transportes por unidad" columnas={['Ítem', 'Detalle']} filas={conceptos(cotizacion.Transportes)} />
                    <SeccionItems titulo="Gastos de viaje y viáticos por unidad" columnas={['Ítem', 'Detalle']} filas={conceptos(cotizacion.Viaticos)} />
                    <SeccionItems titulo="Know-how por unidad" columnas={['Ítem', 'Detalle']} filas={conceptos(cotizacion.KnowHow)} />
                    <SeccionItems titulo="Otros costos por unidad" columnas={['Ítem', 'Detalle']} filas={conceptos(cotizacion.OtrosCostos)} />
                </Stack>
            </Card>

            <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                    <Card withBorder radius="md" p="md" h="100%">
                        <Text fw={600} size="sm" mb="xs">Precio</Text>
                        <Group justify="space-between"><Text size="sm">Precio por unidad</Text><Text size="sm" fw={600}>{formatoMoneda(resultado.PrecioVentaUnitario)}</Text></Group>
                        <Group justify="space-between"><Text size="sm">Número de unidades</Text><Text size="sm" fw={600}>{cotizacion.NumeroUnidades}</Text></Group>
                        <Divider my="xs" />
                        <Group justify="space-between"><Text fw={700}>Precio total</Text><Text fw={700} size="lg" c="blue">{formatoMoneda(resultado.PrecioVentaTotal)}</Text></Group>
                    </Card>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                    <Card withBorder radius="md" p="md" h="100%">
                        <Text fw={600} size="sm" mb="xs">Plazos por unidad</Text>
                        <Group justify="space-between"><Text size="sm">Fabricación</Text><Text size="sm" fw={600}>{cotizacion.TiempoFabricacion} {unidad}</Text></Group>
                        <Group justify="space-between"><Text size="sm">Instalación</Text><Text size="sm" fw={600}>{cotizacion.TiempoInstalacion} {unidad}</Text></Group>
                        <Divider my="xs" />
                        <Group justify="space-between"><Text fw={700}>Total</Text><Text fw={700}>{resultado.TiempoTotal} {unidad}</Text></Group>
                    </Card>
                </Grid.Col>
            </Grid>
        </Stack>
    );
};
